import firebase from '@modules/firebase';
import { logger } from '../logger';
import { getMessageForError } from './util';

type Provider = 'google.com' | 'microsoft.com' | 'apple.com' | 'oidc.oauth2proxy';

interface SignInWithRedirectArgs {
  email: string;
  providerId: Provider;
}

function getOAuthProvider(providerId: Provider) {
  if (providerId === 'google.com') {
    return new firebase.auth.GoogleAuthProvider();
  }
  return new firebase.auth.OAuthProvider(providerId);
}

function isAuthError(error: unknown): error is firebase.auth.Error {
  return typeof error === 'object' && error !== null && 'code' in error && typeof error.code === 'string';
}

class FirebaseAuthService {
  #log = logger.child({ class: 'FirebaseAuthService' });

  async createUserWithEmailAndPassword(email: string, password: string) {
    this.#log.info('Creating user with email and password.', { email });
    try {
      const result = await firebase.auth().createUserWithEmailAndPassword(email, password);

      if (result.user) {
        this.#log.info('Successfully created user with email and password.', {
          provider_id: result.credential?.providerId,
          sign_in_method: result.credential?.signInMethod,
          is_new_user: result.additionalUserInfo?.isNewUser,
        });
      } else {
        this.#log.info('No user found after creation.', { email });
      }

      return result;
    } catch (error) {
      this.#log.error('Error creating user with email and password.', { error, email });

      throw {
        code: isAuthError(error) ? error.code : 'unknown',
        message: isAuthError(error) ? getMessageForError(error) : 'An error occurred, please try again.',
      };
    }
  }

  async signInWithEmailAndPassword(email: string, password: string) {
    this.#log.info('Signing in with email and password.', { email });

    try {
      const result = await firebase.auth().signInWithEmailAndPassword(email, password);

      if (result.user) {
        this.#log.info('Successful sign-in with email and password.', {
          provider_id: result.credential?.providerId,
          sign_in_method: result.credential?.signInMethod,
          is_new_user: result.additionalUserInfo?.isNewUser,
        });
      } else {
        this.#log.info('No sign-in found for email and password.', { email });
      }

      return result;
    } catch (error) {
      this.#log.error('Error signing in with email and password.', { error, email });

      throw {
        code: isAuthError(error) ? error.code : 'unknown',
        message: isAuthError(error) ? getMessageForError(error) : 'An error occurred, please try again.',
      };
    }
  }

  /**
   * Automatically redirects the user to the specified OAuth provider for authentication. This can be a
   * first-time sign up or a sign-in for an existing user.
   */
  async signInWithRedirect({ email, providerId }: SignInWithRedirectArgs) {
    this.#log.info('Redirecting to provider.', { provider_id: providerId, email });

    try {
      const provider = getOAuthProvider(providerId);

      if (email) {
        provider.setCustomParameters({ login_hint: email });
      }

      localStorage.setItem('ssoMethod', providerId);

      // Used to set login loading state immediately upon return.
      localStorage.setItem('isSSOLogin', '1');

      await firebase.auth().signInWithRedirect(provider);

      this.#log.info('Redirected to provider.', { provider_id: providerId });
    } catch (error) {
      this.#log.error('Error redirecting to provider.', {
        error,
        email,
        provider_id: providerId,
      });

      throw {
        code: isAuthError(error) ? error.code : 'unknown',
        message: isAuthError(error) ? getMessageForError(error) : 'An error occurred, please try again.',
      };
    }
  }

  /**
   * Retrieves the signed-in user after a redirect to a social provider. For how a user is redirected to a
   * social provider, see `signInWithRedirect`.
   */
  async getRedirectResult() {
    // These values are set in the `FirebaseAuthService.signInWithRedirect` method.
    const isSsoLogin = localStorage.getItem('isSSOLogin');
    const ssoMethod = localStorage.getItem('ssoMethod');

    this.#log.info('Checking for redirect result.', { is_sso_login: isSsoLogin, sso_method: ssoMethod });

    try {
      const result = await firebase.auth().getRedirectResult();

      if (result.user) {
        this.#log.info('Found user in redirect result.', {
          provider_id: result.credential?.providerId,
          sign_in_method: result.credential?.signInMethod,
          is_new_user: result.additionalUserInfo?.isNewUser,
        });
      } else {
        this.#log.info('No redirect result found.', { is_sso_login: isSsoLogin, sso_method: ssoMethod });
      }

      return result;
    } catch (error) {
      this.#log.error('Error getting redirect result.', {
        error,
        is_sso_login: isSsoLogin,
        sso_method: ssoMethod,
      });

      throw {
        code: isAuthError(error) ? error.code : 'unknown',
        message: isAuthError(error) ? getMessageForError(error) : 'An error occurred, please try again.',
      };
    }
  }
}

export const authService = new FirebaseAuthService();
